import { useRef, useState, type ReactNode } from "react";
import type { PaymentMode } from "@/services/types";
import { Select } from "@/components/base/Select";
import { TextField } from "@/components/base/TextField";
import { Textarea } from "@/components/base/Textarea";
import { DatePicker } from "@/components/base/DatePicker";
import { Button } from "@/components/base/Button";
import { referenceRequired, PAYMENT_MODES } from "./labels";

/** Local "YYYY-MM-DD" — never .toISOString(), which shifts the calendar day in timezones behind UTC (see DatePicker.tsx). */
function todayDateOnly(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export interface PaymentFormValues {
  mode: PaymentMode;
  reference: string;
  receivedOn: string;
  remarks: string;
}

interface PaymentFormProps {
  onSubmit: (values: PaymentFormValues) => Promise<boolean>;
  submitLabel?: string;
  submitIcon?: string;
  defaultMode?: PaymentMode;
  disabled?: boolean;
  amountSummary?: ReactNode;
  compact?: boolean;
  /** Autofocus the amount/mode for speed when opened inline. */
  autoFocus?: boolean;
  availableBalance?: number;
  amountToPay?: number;
}

/**
 * The shared payment-details form (mode, reference, date, remarks). Submit is
 * guarded by a synchronous ref so a double click can never create two receipts —
 * the most expensive bug in this module. Reference is enforced for every mode
 * except cash and member account.
 */
export function PaymentForm({
  onSubmit,
  submitLabel = "Record payment",
  submitIcon = "ri-check-line",
  defaultMode = "cash",
  disabled,
  amountSummary,
  compact,
  autoFocus,
  availableBalance,
  amountToPay,
}: PaymentFormProps) {
  const [mode, setMode] = useState<PaymentMode>(defaultMode);
  const [reference, setReference] = useState("");
  const [receivedOn, setReceivedOn] = useState<string>(todayDateOnly());
  const [remarks, setRemarks] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Synchronous guard — blocks a second click before React can re-render.
  const submittingRef = useRef(false);

  const isInsufficientBalance =
    mode === "member_account" &&
    availableBalance !== undefined &&
    amountToPay !== undefined &&
    availableBalance < amountToPay;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    if (isInsufficientBalance) {
      setError(`Insufficient account balance (₹${availableBalance.toLocaleString("en-IN")}). Please select another payment mode.`);
      return;
    }
    if (referenceRequired(mode) && !reference.trim()) {
      setError("A reference number is required for this payment mode.");
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const ok = await onSubmit({ mode, reference, receivedOn, remarks });
      if (ok) {
        // Reset for the next person in the queue; keep the mode (queue pays alike).
        setReference("");
        setRemarks("");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not record this payment.");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {amountSummary ? <div className="text-sm">{amountSummary}</div> : null}

      {mode === "member_account" && (
        <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50/70 p-2.5 text-xs text-emerald-800">
          <div className="flex items-center gap-2">
            <i className="ri-wallet-3-line text-base text-emerald-600" />
            <span>Deducting directly from member's advance wallet balance</span>
          </div>
          {availableBalance !== undefined ? (
            <span className="font-semibold text-emerald-900">
              Available: ₹{availableBalance.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
            </span>
          ) : null}
        </div>
      )}

      <div className={compact ? "grid grid-cols-1 gap-3 sm:grid-cols-2" : "grid grid-cols-1 gap-3 sm:grid-cols-2"}>
        <Select
          label="Payment mode"
          value={mode}
          onChange={(v) => setMode(v as PaymentMode)}
          options={PAYMENT_MODES.map((m) => ({ value: m.value, label: m.label }))}
        />
        {mode === "member_account" ? (
          <TextField
            label="Account Reference"
            placeholder="Auto-deducted from wallet"
            value="Member Account Balance"
            disabled
          />
        ) : (
          <TextField
            label={referenceRequired(mode) ? "Reference number" : "Reference (optional)"}
            placeholder={referenceRequired(mode) ? "Required" : "Optional"}
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            autoFocus={autoFocus}
          />
        )}
        <DatePicker label="Received on" value={receivedOn} onChange={setReceivedOn} />
        <TextField
          label="Remarks (optional)"
          placeholder="Optional note"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
        />
      </div>
      {error ? <p className="text-xs text-danger-600">{error}</p> : null}
      {isInsufficientBalance ? (
        <p className="text-xs font-medium text-danger-600">
          Cannot pay with Member Account: balance is only ₹{availableBalance?.toLocaleString("en-IN")}. Please collect via Cash, UPI, Bank Transfer, or Cheque.
        </p>
      ) : null}
      <div className="flex items-center justify-end gap-2">
        <Button
          type="submit"
          size="sm"
          icon={<i className={submitIcon} />}
          loading={submitting}
          disabled={disabled || isInsufficientBalance}
        >
          {submitting ? "Recording…" : submitLabel}
        </Button>
      </div>
    </form>
  );
}